import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircleIcon as CheckCircle, WarningCircleIcon as WarningCircle } from "@phosphor-icons/react";
import { GoldDivider, NoiseOverlay } from "@/components/luxury";

/**
 * Public reference-response page.
 *
 * Buyer arrives via the email link `${APP_URL}/verify-reference/{token}`.
 * They see the kennel name, pick "ok" or "concern", optionally leave a
 * private comment, and submit. The kennel never sees who said what.
 */
export default function VerifyReference() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [outcome, setOutcome] = useState("ok");
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/kennel-verification/reference/${token}`)
      .then((r) => { if (!cancelled) setState({ loading: false, error: null, data: r.data }); })
      .catch((e) => { if (!cancelled) setState({ loading: false, error: e?.response?.data?.detail || "Invalid link", data: null }); });
    return () => { cancelled = true; };
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/kennel-verification/reference/${token}`, { outcome, comment });
      setDone(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Could not submit your response.";
      setState((s) => ({ ...s, error: msg }));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-ink font-manrope flex flex-col">
      <Helmet><title>Reference request — Armstrong K9 Registry</title></Helmet>
      <header className="bg-ink relative overflow-hidden">
        <NoiseOverlay opacity={0.08} />
        <div className="max-w-3xl mx-auto px-6 py-10 text-white relative">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold-light mb-2">Armstrong K9 Registry</p>
          <h1 className="font-spectral font-light text-3xl md:text-4xl tracking-tight">Reference request</h1>
        </div>
      </header>

      <main className="flex-1 max-w-3xl mx-auto px-6 py-12 w-full">
        {state.loading && <p className="text-sm text-[#6B6B6B]">Loading…</p>}
        {state.error && (
          <div className="bg-white border border-[#EAEAEA] rounded-xl p-8 text-center">
            <WarningCircle size={32} weight="duotone" className="mx-auto text-[#A4222A] mb-3" />
            <h2 className="font-spectral text-xl mb-2">Link not valid</h2>
            <p className="text-sm text-[#6B6B6B]">{state.error}</p>
          </div>
        )}

        {state.data?.already_responded && (
          <div className="bg-white border border-[#EAEAEA] rounded-xl p-8 text-center">
            <CheckCircle size={32} weight="fill" className="mx-auto text-[#8A6A00] mb-3" />
            <h2 className="font-spectral text-xl mb-2">Thanks — already received</h2>
            <p className="text-sm text-[#6B6B6B]">
              Your response for <strong>{state.data.kennel_name}</strong> is on file.
            </p>
          </div>
        )}

        {done && (
          <div className="bg-white border border-[#EAEAEA] rounded-xl p-8 text-center" data-testid="reference-thanks">
            <CheckCircle size={32} weight="fill" className="mx-auto text-[#8A6A00] mb-3" />
            <h2 className="font-spectral text-xl mb-2">Thank you</h2>
            <p className="text-sm text-[#6B6B6B]">
              Your response is private — the kennel won't know who we asked or what you said.
            </p>
          </div>
        )}

        {state.data && !state.data.already_responded && !done && (
          <form onSubmit={submit} className="bg-white border border-[#EAEAEA] rounded-xl p-8 space-y-6" data-testid="reference-form">
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-gold mb-2">For our verification team</p>
              <h2 className="font-spectral text-2xl tracking-tight leading-tight mb-3">
                Quick reference for <span className="text-crimson">{state.data.kennel_name}</span>
              </h2>
              <p className="text-sm text-[#3a3a3a] leading-relaxed">
                We're reviewing this kennel for our verified seal. You bought a dog from
                them recently — we'd love a brief, honest take. The kennel never sees
                who we asked, or what was said.
              </p>
            </div>

            <GoldDivider className="my-6" />

            <div>
              <label className="text-sm font-bold mb-3 block">Would you buy from this kennel again?</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setOutcome("ok")}
                  data-testid="ref-outcome-ok"
                  className={`text-left p-4 rounded-xl border transition-all ${
                    outcome === "ok"
                      ? "border-gold/60 bg-[#FBF7EC] shadow-wax-seal"
                      : "border-[#E5E5E5] bg-white hover:border-gold/40"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle size={18} weight="fill" className="text-[#8A6A00]" />
                    <span className="font-bold text-sm">Yes, I had a good experience</span>
                  </div>
                  <p className="text-xs text-[#6B6B6B]">No serious concerns to flag.</p>
                </button>
                <button
                  type="button"
                  onClick={() => setOutcome("concern")}
                  data-testid="ref-outcome-concern"
                  className={`text-left p-4 rounded-xl border transition-all ${
                    outcome === "concern"
                      ? "border-crimson/60 bg-[#FBEBEC]"
                      : "border-[#E5E5E5] bg-white hover:border-crimson/40"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <WarningCircle size={18} weight="duotone" className="text-[#A4222A]" />
                    <span className="font-bold text-sm">I have concerns</span>
                  </div>
                  <p className="text-xs text-[#6B6B6B]">Something we should know about.</p>
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="comment" className="text-sm font-bold mb-2 block">
                Comment <span className="text-[#6B6B6B] font-normal">(optional, private to admins)</span>
              </label>
              <textarea
                id="comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={4}
                maxLength={2000}
                placeholder={outcome === "ok" ? "Anything that stood out as exceptional?" : "What happened? The more detail the better."}
                className="w-full text-sm border border-[#E5E5E5] rounded-md p-3 focus:outline-none focus:border-gold"
                data-testid="ref-comment"
              />
            </div>

            <Button
              type="submit"
              disabled={submitting}
              className="rounded-full bg-ink hover:bg-crimson text-white h-11 px-7 w-full sm:w-auto"
              data-testid="ref-submit"
            >
              {submitting ? "Submitting…" : "Submit my response"}
            </Button>
          </form>
        )}
      </main>

      <footer className="border-t border-[#EAEAEA] py-6 text-center text-xs text-[#6B6B6B]">
        © {new Date().getFullYear()} Armstrong K9 Registry · Your response is private.
      </footer>
    </div>
  );
}
